import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useLanguage } from "../context/LanguageContext";
import {
  BookOpen, CheckCircle, ChevronDown, ChevronUp, ChevronLeft,
  Clock, Star, Brain, Lightbulb, AlertTriangle, Flame, Trophy,
  Target, Zap, Award, MapPin, Users, Calendar, Eye, EyeOff,
  Search, Filter, Play, BarChart3, CircleDot, Crown, Building2,
  Landmark, Globe, Swords, Compass, Flag, Mountain, Wheat,
  Trees, Waves, Snowflake, ChevronRight, X, HelpCircle,
  GraduationCap, Sparkles, ArrowRight, Shield, Scale, Vote,
  Building, Layers, LayoutGrid
} from "lucide-react";

const CHAPTER_ICONS = {
  1: Globe,
  2: Compass,
  3: Flag,
  4: Swords,
  5: Crown,
  6: Landmark,
  7: Building2,
  8: Scale,
  9: Users,
  10: Mountain,
  11: Star,
};


const CHAPTER_COLORS = {
  1: { bg: "bg-blue-50", border: "border-blue-200", icon: "text-blue-600", badge: "bg-blue-100" },
  2: { bg: "bg-indigo-50", border: "border-indigo-200", icon: "text-indigo-600", badge: "bg-indigo-100" },
  3: { bg: "bg-red-50", border: "border-red-200", icon: "text-red-600", badge: "bg-red-100" },
  4: { bg: "bg-amber-50", border: "border-amber-200", icon: "text-amber-700", badge: "bg-amber-100" },
  5: { bg: "bg-purple-50", border: "border-purple-200", icon: "text-purple-600", badge: "bg-purple-100" },
  6: { bg: "bg-emerald-50", border: "border-emerald-200", icon: "text-emerald-600", badge: "bg-emerald-100" },
  7: { bg: "bg-cyan-50", border: "border-cyan-200", icon: "text-cyan-600", badge: "bg-cyan-100" },
  8: { bg: "bg-slate-50", border: "border-slate-200", icon: "text-slate-600", badge: "bg-slate-100" },
  9: { bg: "bg-pink-50", border: "border-pink-200", icon: "text-pink-600", badge: "bg-pink-100" },
  10: { bg: "bg-teal-50", border: "border-teal-200", icon: "text-teal-600", badge: "bg-teal-100" },
  11: { bg: "bg-orange-50", border: "border-orange-200", icon: "text-orange-600", badge: "bg-orange-100" },
};

const LEVEL_NAMES = [
  "Newcomer", "Explorer", "Settler", "Voyageur", "Pioneer",
  "Patriot", "Citizen Scholar", "History Buff", "Nation Builder",
  "Confederation Master", "True Canadian"
];

const XP_PER_LEVEL = 500;

const ERA_COLORS = {
  "Exploration": "blue",
  "Colonial": "indigo",
  "Nation Building": "amber",
  "World Wars": "red",
  "Modern": "green",
  "Pre-Contact": "purple",
};

const ROLE_COLORS = {
  "Explorer": "blue",
  "Military": "red",
  "Political": "indigo",
  "Cultural": "amber",
  "Scientific": "green",
};

const DIFFICULTY_STYLES = {
  Easy: "bg-green-100 text-green-800",
  Medium: "bg-amber-100 text-amber-800",
  Hard: "bg-red-100 text-red-800",
};

const FREQ_BADGE = {
  high: { label: "Frequently Tested", cls: "bg-red-100 text-red-700" },
  medium: { label: "Sometimes Tested", cls: "bg-amber-100 text-amber-700" },
  low: { label: "Occasionally Tested", cls: "bg-gray-100 text-gray-600" },
};

const REGIONS = [
  { id: "atlantic", name: "Atlantic Canada", color: "#3b82f6", provinces: ["Nova Scotia", "New Brunswick", "Prince Edward Island", "Newfoundland and Labrador"], facts: "First region explored by Europeans. Rich maritime heritage." },
  { id: "central", name: "Central Canada", color: "#22c55e", provinces: ["Ontario", "Quebec"], facts: "Most populous region. Seat of federal government in Ottawa." },
  { id: "prairies", name: "Prairie Provinces", color: "#eab308", provinces: ["Manitoba", "Saskatchewan", "Alberta"], facts: "Breadbasket of Canada. Rich in agriculture and natural resources." },
  { id: "westcoast", name: "West Coast", color: "#14b8a6", provinces: ["British Columbia"], facts: "Gateway to the Pacific. Diverse geography from rainforests to mountains." },
  { id: "north", name: "Northern Canada", color: "#a855f7", provinces: ["Yukon", "Northwest Territories", "Nunavut"], facts: "Largest area, smallest population. Home to many Indigenous communities." },
];

const CONFEDERATION_TIMELINE = [
  { year: 1867, name: "Ontario", region: "central" },
  { year: 1867, name: "Quebec", region: "central" },
  { year: 1867, name: "Nova Scotia", region: "atlantic" },
  { year: 1867, name: "New Brunswick", region: "atlantic" },
  { year: 1870, name: "Manitoba", region: "prairies" },
  { year: 1871, name: "British Columbia", region: "westcoast" },
  { year: 1873, name: "Prince Edward Island", region: "atlantic" },
  { year: 1905, name: "Saskatchewan", region: "prairies" },
  { year: 1905, name: "Alberta", region: "prairies" },
  { year: 1949, name: "Newfoundland", region: "atlantic" },
  { year: 1999, name: "Nunavut", region: "north" },
];

const COMPARISONS = [
  {
    title: "Head of State vs Head of Government",
    headers: ["Aspect", "Head of State", "Head of Government"],
    rows: [
      ["Title", "King/Queen (represented by Governor General)", "Prime Minister"],
      ["Selection", "Hereditary / Appointed by Monarch", "Leader of party with most seats"],
      ["Role", "Ceremonial, represents Canada", "Runs the government day-to-day"],
      ["Power", "Symbolic (Royal Assent)", "Executive power, sets policy"],
    ],
  },
  {
    title: "Rights vs Responsibilities",
    headers: ["Rights (Charter)", "Responsibilities"],
    rows: [
      ["Freedom of expression", "Obey Canadian laws"],
      ["Right to vote", "Serve on a jury when called"],
      ["Mobility rights", "Respect others' rights"],
      ["Legal rights", "Participate in the democratic process"],
      ["Equality rights", "Help others in the community"],
    ],
  },
  {
    title: "Common Law vs Civil Code",
    headers: ["Aspect", "Common Law", "Civil Code"],
    rows: [
      ["Used in", "All provinces except Quebec", "Quebec"],
      ["Based on", "Precedent (judge decisions)", "Written legal code"],
      ["Origin", "English legal tradition", "French legal tradition (Napoleonic Code)"],
      ["Approach", "Case-by-case interpretation", "Comprehensive written rules"],
    ],
  },
  {
    title: "Federal vs Provincial vs Municipal",
    headers: ["Federal", "Provincial", "Municipal"],
    rows: [
      ["Defence & military", "Education", "Local transit"],
      ["Foreign policy", "Healthcare", "Snow removal"],
      ["Criminal law", "Natural resources", "Libraries"],
      ["Banking", "Property & civil rights", "Local police/fire"],
      ["Citizenship", "Highways", "Zoning & permits"],
    ],
  },
  {
    title: "First Nations vs Inuit vs Métis",
    headers: ["Aspect", "First Nations", "Inuit", "Métis"],
    rows: [
      ["Location", "Across Canada (south of Arctic)", "Arctic regions", "Prairies, Ontario, BC"],
      ["Origin", "Original inhabitants", "Arctic Indigenous peoples", "Mixed Indigenous-European heritage"],
      ["Languages", "Many (Cree, Ojibwe, etc.)", "Inuktitut, Inuinnaqtun", "Michif, French, Cree"],
      ["Governance", "Band councils, treaties", "Inuit Tapiriit Kanatami", "Métis National Council"],
    ],
  },
];

// Simplified Canada map SVG paths by region
const MAP_PATHS = {
  atlantic: "M 580 180 Q 600 160 620 170 L 640 190 Q 650 210 630 220 L 610 230 Q 590 220 580 200 Z",
  central: "M 380 200 L 450 180 Q 500 170 540 180 L 570 200 Q 580 230 560 260 L 500 280 Q 440 270 400 250 L 380 230 Z",
  prairies: "M 250 180 L 320 170 Q 360 175 380 190 L 380 250 Q 360 260 320 255 L 260 240 Q 240 220 250 200 Z",
  westcoast: "M 180 180 L 220 170 Q 240 175 250 190 L 250 250 Q 240 270 220 260 L 190 240 Q 175 220 180 200 Z",
  north: "M 220 50 Q 300 30 400 40 L 500 50 Q 550 70 540 120 L 500 150 Q 450 160 380 160 L 300 155 Q 240 140 230 110 L 220 80 Z",
};

function ReadinessGauge({ score, size = 120 }) {
  const radius = (size - 16) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  const color = score >= 75 ? "#22c55e" : score >= 60 ? "#eab308" : "#ef4444";
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} stroke="#e5e7eb" strokeWidth="8" fill="none" />
        <circle cx={size/2} cy={size/2} r={radius} stroke={color} strokeWidth="8" fill="none"
          strokeDasharray={circumference} strokeDashoffset={strokeDashoffset}
          strokeLinecap="round" className="transition-all duration-700" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold" style={{ color }}>{score}%</span>
        <span className="text-xs text-gray-500">Ready</span>
      </div>
    </div>
  );
}

function ProgressRing({ progress, size = 40 }) {
  const r = (size - 6) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - progress * c;
  const done = progress >= 1;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={r} stroke="#e5e7eb" strokeWidth="3" fill="none" />
        <circle cx={size/2} cy={size/2} r={r} stroke={done ? "#22c55e" : "#3b82f6"} strokeWidth="3" fill="none"
          strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round" />
      </svg>
      {done && (
        <div className="absolute inset-0 flex items-center justify-center">
          <CheckCircle className="w-5 h-5 text-green-500" />
        </div>
      )}
    </div>
  );
}

function XPBar({ xp, level }) {
  const levelObj = typeof level === "object" ? level : { name: LEVEL_NAMES[Math.min(level || 0, LEVEL_NAMES.length - 1)], progress: 0 };
  const levelName = levelObj.name || "Newcomer";
  const progress = typeof levelObj.progress === "number" ? levelObj.progress * 100 : ((xp % XP_PER_LEVEL) / XP_PER_LEVEL) * 100;
  const nextLevel = levelObj.next_level || "";
  const nextXP = levelObj.next_level_xp || XP_PER_LEVEL;
  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
          <Zap className="w-4 h-4 text-amber-500" /> {levelName}
        </span>
        <span className="text-xs text-gray-500">{xp}/{nextXP} XP</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2.5">
        <div className="bg-amber-500 h-2.5 rounded-full transition-all duration-500" style={{ width: `${Math.min(progress, 100)}%` }} />
      </div>
      {nextLevel && <span className="text-xs text-gray-400 mt-0.5">Next: {nextLevel}</span>}
    </div>
  );
}

function DailyChallengeModal({ auth, onClose }) {
  const [question, setQuestion] = useState(null);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/gamification/daily-challenge`, { headers: { Authorization: `Bearer ${auth?.token}` } })
      .then(r => setQuestion(r.data))
      .catch(() => setQuestion(null))
      .finally(() => setLoading(false));
  }, [auth]);

  const submit = async () => {
    if (selected === null) return;
    try {
      const r = await axios.post(`${API}/gamification/daily-challenge/answer`, { answer: selected }, { headers: { Authorization: `Bearer ${auth?.token}` } });
      setResult(r.data);
    } catch { setResult({ correct: false, message: "Error submitting answer." }); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <Card className="w-full max-w-lg p-6 relative" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        <h3 className="text-lg font-bold flex items-center gap-2 mb-4"><Trophy className="w-5 h-5 text-amber-500" /> Daily Challenge</h3>
        {loading ? <p className="text-gray-500">Loading...</p> : !question ? <p className="text-gray-500">No challenge available today. Check back tomorrow!</p> : (
          <div>
            <p className="font-medium mb-4">{question.question}</p>
            <div className="space-y-2 mb-4">
              {question.options?.map((opt, i) => (
                <button key={i} onClick={() => !result && setSelected(i)}
                  className={`w-full text-left p-3 rounded-lg border transition ${selected === i ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-gray-300"} ${result ? (i === question.correct_answer ? "border-green-500 bg-green-50" : selected === i ? "border-red-500 bg-red-50" : "") : ""}`}>
                  {opt}
                </button>
              ))}
            </div>
            {!result ? (
              <Button onClick={submit} disabled={selected === null} className="w-full">Submit Answer</Button>
            ) : (
              <div className={`p-3 rounded-lg ${result.correct ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`}>
                <p className="font-medium">{result.correct ? "Correct! +25 XP" : "Not quite!"}</p>
                {result.explanation && <p className="text-sm mt-1">{result.explanation}</p>}
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

function MiniQuiz({ chapterId, auth, onComplete }) {
  const [questions, setQuestions] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/reading-progress/mini-quiz/${chapterId}/questions`, { headers: { Authorization: `Bearer ${auth?.token}` } })
      .then(r => setQuestions(r.data?.questions || r.data))
      .catch(() => setQuestions(null))
      .finally(() => setLoading(false));
  }, [chapterId, auth]);

  const submit = async () => {
    try {
      const r = await axios.post(`${API}/reading-progress/mini-quiz/${chapterId}`, { answers }, { headers: { Authorization: `Bearer ${auth?.token}` } });
      setResult(r.data);
      if (r.data?.passed && onComplete) onComplete();
    } catch { setResult({ score: 0, passed: false, message: "Error submitting quiz." }); }
  };

  if (loading) return <p className="text-gray-500 p-4">Loading quiz...</p>;
  if (!questions) return <p className="text-gray-500 p-4">No quiz questions available for this chapter.</p>;

  return (
    <Card className="p-5 mt-4 border-2 border-blue-200 bg-blue-50/30">
      <h4 className="font-bold text-lg flex items-center gap-2 mb-4"><HelpCircle className="w-5 h-5 text-blue-600" /> Check Your Understanding</h4>
      <div className="space-y-5">
        {questions.map((q, qi) => (
          <div key={qi}>
            <p className="font-medium mb-2">{qi + 1}. {q.question}</p>
            <div className="space-y-1.5 ml-2">
              {q.options?.map((opt, oi) => (
                <label key={oi} className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-white transition ${result ? (oi === q.correct_answer ? "bg-green-100" : answers[qi] === oi ? "bg-red-100" : "") : ""}`}>
                  <input type="radio" name={`q${qi}`} checked={answers[qi] === oi} onChange={() => !result && setAnswers(p => ({ ...p, [qi]: oi }))} disabled={!!result} className="accent-blue-600" />
                  <span className="text-sm">{opt}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
      {!result ? (
        <Button onClick={submit} disabled={Object.keys(answers).length < questions.length} className="mt-4">Submit Quiz</Button>
      ) : (
        <div className={`mt-4 p-4 rounded-lg ${result.passed ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
          <p className="font-bold">{result.passed ? "Passed!" : "Not quite — review and try again."} Score: {result.score}/{questions.length}</p>
          {result.passed && <p className="text-sm mt-1">Chapter mastered! +50 XP</p>}
        </div>
      )}
    </Card>
  );
}

function GovernmentDiagram() {
  const [selected, setSelected] = useState(null);
  const nodes = {
    monarch: { label: "King/Queen", sub: "Head of State", detail: "The reigning monarch of Canada. Currently King Charles III. Role is mostly ceremonial.", x: 350, y: 30, color: "#7c3aed" },
    gg: { label: "Governor General", sub: "Represents the Crown", detail: "Appointed by the Monarch on advice of PM. Gives Royal Assent to bills. Commander-in-Chief of the Canadian Forces.", x: 350, y: 110, color: "#7c3aed" },
    pm: { label: "Prime Minister", sub: "Head of Government", detail: "Leader of the party with the most seats in the House of Commons. Selects Cabinet ministers. Sets government policy and agenda.", x: 350, y: 200, color: "#2563eb" },
    cabinet: { label: "Cabinet", sub: "Ministers", detail: "Selected by PM from elected MPs (usually). Each minister oversees a government department (Health, Finance, Defence, etc.).", x: 350, y: 290, color: "#2563eb" },
    commons: { label: "House of Commons", sub: "338 MPs (Elected)", detail: "Lower house of Parliament. Members of Parliament elected by citizens in ridings across Canada. Proposes and votes on laws.", x: 200, y: 380, color: "#059669" },
    senate: { label: "Senate", sub: "105 Senators (Appointed)", detail: "Upper house of Parliament. Senators appointed by the Governor General on advice of the PM. Reviews and can amend bills.", x: 500, y: 380, color: "#059669" },
    federal: { label: "Federal", sub: "Defence, Foreign Policy, Criminal Law, Banking", detail: "Responsibilities include national defence, foreign affairs, criminal law, currency, banking, Indigenous affairs, citizenship.", x: 150, y: 490, color: "#0891b2" },
    provincial: { label: "Provincial", sub: "Education, Healthcare, Resources", detail: "Responsible for education, healthcare, natural resources, property rights, highways, provincial policing.", x: 350, y: 490, color: "#0891b2" },
    municipal: { label: "Municipal", sub: "Transit, Libraries, Local Services", detail: "Handle local transit, water, sewage, snow removal, parks, libraries, local police and fire services.", x: 550, y: 490, color: "#0891b2" },
  };
  const lines = [
    ["monarch", "gg"], ["gg", "pm"], ["pm", "cabinet"],
    ["cabinet", "commons"], ["cabinet", "senate"],
  ];

  return (
    <div className="relative">
      <h3 className="font-bold text-lg mb-3 flex items-center gap-2"><Landmark className="w-5 h-5 text-indigo-600" /> Government Structure</h3>
      <p className="text-sm text-gray-500 mb-3">Click any box to learn more</p>
      <div className="overflow-x-auto">
        <svg viewBox="0 0 700 560" className="w-full max-w-2xl mx-auto">
          {lines.map(([a, b], i) => (
            <line key={i} x1={nodes[a].x} y1={nodes[a].y + 25} x2={nodes[b].x} y2={nodes[b].y - 5} stroke="#cbd5e1" strokeWidth="2" />
          ))}
          <line x1={150} y1={450} x2={550} y2={450} stroke="#0891b2" strokeWidth="2" strokeDasharray="6 3" />
          <text x={350} y={465} textAnchor="middle" className="text-xs fill-cyan-700 font-medium">Three Levels of Government</text>
          {Object.entries(nodes).map(([key, n]) => (
            <g key={key} onClick={() => setSelected(selected === key ? null : key)} className="cursor-pointer">
              <rect x={n.x - 85} y={n.y - 5} width={170} height={50} rx={8}
                fill={selected === key ? n.color : "white"} stroke={n.color} strokeWidth={selected === key ? 2 : 1.5}
                className="transition-all" />
              <text x={n.x} y={n.y + 15} textAnchor="middle"
                className={`text-sm font-semibold ${selected === key ? "fill-white" : ""}`}
                style={{ fill: selected === key ? "white" : n.color }}>{n.label}</text>
              <text x={n.x} y={n.y + 32} textAnchor="middle"
                className="text-[10px]"
                style={{ fill: selected === key ? "rgba(255,255,255,0.8)" : "#6b7280" }}>{n.sub}</text>
            </g>
          ))}
        </svg>
      </div>
      {selected && nodes[selected] && (
        <div className="mt-3 p-4 rounded-lg border-l-4" style={{ borderColor: nodes[selected].color, background: nodes[selected].color + "10" }}>
          <h4 className="font-bold" style={{ color: nodes[selected].color }}>{nodes[selected].label}</h4>
          <p className="text-sm text-gray-700 mt-1">{nodes[selected].detail}</p>
        </div>
      )}
    </div>
  );
}

function CanadaMap() {
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [quizMode, setQuizMode] = useState(false);
  const [quizTarget, setQuizTarget] = useState(null);
  const [quizResult, setQuizResult] = useState(null);
  const [quizScore, setQuizScore] = useState({ correct: 0, total: 0 });

  const startQuiz = () => {
    setQuizMode(true);
    setQuizScore({ correct: 0, total: 0 });
    nextQuizQuestion();
  };

  const nextQuizQuestion = () => {
    const r = REGIONS[Math.floor(Math.random() * REGIONS.length)];
    const prov = r.provinces[Math.floor(Math.random() * r.provinces.length)];
    setQuizTarget({ region: r.id, province: prov });
    setQuizResult(null);
  };

  const handleRegionClick = (regionId) => {
    if (quizMode && quizTarget) {
      const correct = regionId === quizTarget.region;
      setQuizResult(correct);
      setQuizScore(s => ({ correct: s.correct + (correct ? 1 : 0), total: s.total + 1 }));
      setTimeout(() => nextQuizQuestion(), 1500);
    } else {
      setSelectedRegion(selectedRegion === regionId ? null : regionId);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-lg flex items-center gap-2"><MapPin className="w-5 h-5 text-green-600" /> Canada Map</h3>
        <Button variant={quizMode ? "default" : "outline"} size="sm" onClick={() => { if (quizMode) { setQuizMode(false); setQuizTarget(null); } else startQuiz(); }}>
          {quizMode ? "Exit Quiz" : "Quiz Mode"}
        </Button>
      </div>
      {quizMode && quizTarget && (
        <div className="mb-3 p-3 bg-blue-50 rounded-lg text-center">
          <p className="font-medium">Where is <span className="text-blue-700 font-bold">{quizTarget.province}</span>?</p>
          <p className="text-xs text-gray-500">Click the correct region on the map</p>
          {quizResult !== null && (
            <p className={`mt-1 font-bold ${quizResult ? "text-green-600" : "text-red-600"}`}>{quizResult ? "Correct!" : "Try again!"}</p>
          )}
          <p className="text-xs text-gray-400 mt-1">Score: {quizScore.correct}/{quizScore.total}</p>
        </div>
      )}
      <div className="overflow-x-auto">
        <svg viewBox="140 20 540 310" className="w-full max-w-xl mx-auto">
          {REGIONS.map(r => (
            <path key={r.id} d={MAP_PATHS[r.id]} fill={r.color + "33"} stroke={r.color} strokeWidth="2"
              className="cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => handleRegionClick(r.id)} />
          ))}
          {REGIONS.map(r => {
            const match = MAP_PATHS[r.id].match(/M\s+([\d.]+)\s+([\d.]+)/);
            if (!match) return null;
            const pathPoints = MAP_PATHS[r.id].match(/[\d.]+/g).map(Number);
            let cx = 0, cy = 0, count = 0;
            for (let i = 0; i < pathPoints.length; i += 2) { cx += pathPoints[i]; cy += pathPoints[i+1]; count++; }
            cx /= count; cy /= count;
            return <text key={r.id + "t"} x={cx} y={cy} textAnchor="middle" className="text-[9px] font-medium pointer-events-none" fill={r.color}>{r.name.split(" ")[0]}</text>;
          })}
        </svg>
      </div>
      {!quizMode && selectedRegion && (
        <div className="mt-3 p-4 rounded-lg border" style={{ borderColor: REGIONS.find(r => r.id === selectedRegion)?.color }}>
          {(() => {
            const r = REGIONS.find(rg => rg.id === selectedRegion);
            return r ? (
              <>
                <h4 className="font-bold" style={{ color: r.color }}>{r.name}</h4>
                <p className="text-sm text-gray-600 mt-1">{r.facts}</p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {r.provinces.map(p => <span key={p} className="text-xs px-2 py-1 rounded-full bg-gray-100">{p}</span>)}
                </div>
              </>
            ) : null;
          })()}
        </div>
      )}
    </div>
  );
}

function ComparisonCards() {
  const [open, setOpen] = useState(0);
  return (
    <div>
      <h3 className="font-bold text-lg flex items-center gap-2 mb-3"><Scale className="w-5 h-5 text-blue-600" /> Comparison Tables</h3>
      <div className="space-y-3">
        {COMPARISONS.map((comp, i) => (
          <Card key={i} className="overflow-hidden">
            <button className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50" onClick={() => setOpen(open === i ? -1 : i)}>
              <span className="font-medium">{comp.title}</span>
              {open === i ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {open === i && (
              <div className="px-4 pb-4 overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr>{comp.headers.map((h, hi) => <th key={hi} className="text-left p-2 bg-gray-50 border font-medium">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {comp.rows.map((row, ri) => (
                      <tr key={ri}>{row.map((cell, ci) => <td key={ci} className="p-2 border">{cell}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

function ConfederationTimeline() {
  const regionColor = (r) => REGIONS.find(rg => rg.id === r)?.color || "#6b7280";
  return (
    <div>
      <h3 className="font-bold text-lg flex items-center gap-2 mb-3"><Clock className="w-5 h-5 text-amber-600" /> Confederation Timeline</h3>
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-4 min-w-max px-2 py-4">
          {CONFEDERATION_TIMELINE.map((item, i) => (
            <div key={i} className="flex flex-col items-center">
              <div className="w-28 rounded-lg border-2 p-3 text-center bg-white" style={{ borderColor: regionColor(item.region) }}>
                <div className="text-xs font-bold" style={{ color: regionColor(item.region) }}>{item.year}</div>
                <div className="text-sm font-medium mt-1">{item.name}</div>
              </div>
              {i < CONFEDERATION_TIMELINE.length - 1 && <ArrowRight className="w-4 h-4 text-gray-300 mt-2 rotate-0" style={{ position: "absolute", marginLeft: "140px", marginTop: "20px" }} />}
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        {REGIONS.map(r => (
          <span key={r.id} className="text-xs flex items-center gap-1">
            <span className="w-3 h-3 rounded-full inline-block" style={{ background: r.color }} />{r.name}
          </span>
        ))}
      </div>
    </div>
  );
}


function StudyHubInner({ auth }) {
  const { t } = useLanguage();
  const [mainTab, setMainTab] = useState("chapters");
  const [chapters, setChapters] = useState([]);
  const [dates, setDates] = useState([]);
  const [people, setPeople] = useState([]);
  const [progress, setProgress] = useState({});
  const [xpData, setXpData] = useState({ xp: 0, level: 0, badges: [] });
  const [readiness, setReadiness] = useState(0);
  const [streak, setStreak] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [chapterContent, setChapterContent] = useState(null);
  const [chapterLoading, setChapterLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [chapterTab, setChapterTab] = useState("content");
  const [showDailyChallenge, setShowDailyChallenge] = useState(false);
  const [showMiniQuiz, setShowMiniQuiz] = useState(false);
  const [eraFilter, setEraFilter] = useState("All");
  const [roleFilter, setRoleFilter] = useState("All");
  const [dateSearch, setDateSearch] = useState("");
  const [peopleSearch, setPeopleSearch] = useState("");
  const [visualTab, setVisualTab] = useState("government");

  const headers = { Authorization: `Bearer ${auth?.token}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [chRes, dRes, pRes, prRes, xpRes, rdRes, stRes] = await Promise.allSettled([
        axios.get(`${API}/chapters`, { headers }),
        axios.get(`${API}/important-dates`, { headers }),
        axios.get(`${API}/important-personalities`, { headers }),
        axios.get(`${API}/reading-progress`, { headers }),
        axios.get(`${API}/gamification/xp`, { headers }),
        axios.get(`${API}/gamification/readiness`, { headers }),
        axios.get(`${API}/streak`, { headers }),
      ]);
      if (chRes.status === "fulfilled") setChapters(chRes.value.data?.chapters || chRes.value.data || []);
      if (dRes.status === "fulfilled") setDates(dRes.value.data?.dates || dRes.value.data || []);
      if (pRes.status === "fulfilled") setPeople(pRes.value.data?.personalities || pRes.value.data || []);
      if (prRes.status === "fulfilled") {
        const pd = prRes.value.data;
        const pm = {};
        if (Array.isArray(pd)) pd.forEach(p => { pm[p.chapter_id] = p; });
        else if (pd?.progress) Object.assign(pm, pd.progress);
        else Object.assign(pm, pd || {});
        setProgress(pm);
      }
      if (xpRes.status === "fulfilled") { const xd = xpRes.value.data || {}; setXpData({ xp: xd.total_xp || xd.xp || 0, level: typeof xd.level === "object" ? xd.level : { name: "Newcomer", progress: 0 }, badges: xd.badges || [], badge_count: xd.badge_count || 0 }); }
      if (rdRes.status === "fulfilled") setReadiness(rdRes.value.data?.score ?? rdRes.value.data?.readiness ?? 0);
      if (stRes.status === "fulfilled") setStreak(stRes.value.data?.current_streak ?? stRes.value.data?.streak ?? 0);
    } catch (e) { console.error("StudyHub fetch error:", e); }
    setLoading(false);
  }, [auth]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openChapter = async (chapter) => {
    setSelectedChapter(chapter);
    setChapterLoading(true);
    setChapterTab("content");
    setExpandedSections({});
    setShowMiniQuiz(false);
    try {
      const r = await axios.get(`${API}/chapters/${chapter.id}/content`, { headers });
      setChapterContent(r.data);
    } catch { setChapterContent(null); }
    setChapterLoading(false);
  };

  const markSectionRead = async (chapterId, sectionIndex) => {
    try {
      await axios.post(`${API}/reading-progress/mark-read`, { chapter_id: chapterId, section_index: sectionIndex }, { headers });
      await axios.post(`${API}/gamification/xp/award`, { action: "read_section", chapter_id: chapterId }, { headers });
      // refresh progress
      const prRes = await axios.get(`${API}/reading-progress`, { headers });
      const pd = prRes.data;
      const pm = {};
      if (Array.isArray(pd)) pd.forEach(p => { pm[p.chapter_id] = p; });
      else if (pd?.progress) Object.assign(pm, pd.progress);
      else Object.assign(pm, pd || {});
      setProgress(pm);
      const xpRes = await axios.get(`${API}/gamification/xp`, { headers });
      { const xd = xpRes.data || {}; setXpData({ xp: xd.total_xp || xd.xp || 0, level: typeof xd.level === "object" ? xd.level : { name: "Newcomer", progress: 0 }, badges: xd.badges || [], badge_count: xd.badge_count || 0 }); }
    } catch (e) { console.error("Mark read error:", e); }
  };

  const getChapterProgress = (chapterId) => {
    const p = progress[chapterId];
    if (!p) return 0;
    const read = p.sections_read?.length || p.read_count || 0;
    const total = p.total_sections || chapters.find(c => c.id === chapterId)?.section_count || 1;
    return total > 0 ? read / total : 0;
  };

  const chaptersComplete = chapters.filter(c => getChapterProgress(c.id) >= 1).length;

  // --- DATES TAB helpers ---
  const eras = ["All", ...new Set(dates.map(d => d.era).filter(Boolean))];
  const filteredDates = dates.filter(d => {
    if (eraFilter !== "All" && d.era !== eraFilter) return false;
    if (dateSearch && !JSON.stringify(d).toLowerCase().includes(dateSearch.toLowerCase())) return false;
    return true;
  });
  const groupedDates = {};
  filteredDates.forEach(d => {
    const era = d.era || "Other";
    if (!groupedDates[era]) groupedDates[era] = [];
    groupedDates[era].push(d);
  });

  // --- PEOPLE TAB helpers ---
  const roles = ["All", ...new Set(people.map(p => p.role).filter(Boolean))];
  const filteredPeople = people.filter(p => {
    if (roleFilter !== "All" && p.role !== roleFilter) return false;
    if (peopleSearch && !JSON.stringify(p).toLowerCase().includes(peopleSearch.toLowerCase())) return false;
    return true;
  });
  const groupedPeople = {};
  filteredPeople.forEach(p => {
    const era = p.era || "Other";
    if (!groupedPeople[era]) groupedPeople[era] = [];
    groupedPeople[era].push(p);
  });

  // ===== CHAPTER DETAIL VIEW =====
  if (selectedChapter) {
    const cp = progress[selectedChapter.id];
    const sectionsRead = cp?.sections_read || [];
    const sections = chapterContent?.sections || [];
    const totalSections = sections.length || selectedChapter.section_count || 0;
    const readCount = sectionsRead.length;
    const keyPoints = chapterContent?.key_points || selectedChapter.key_points || [];
    const studyTips = chapterContent?.study_tips || [];
    const mnemonics = chapterContent?.mnemonics || [];
    const difficulty = chapterContent?.difficulty || selectedChapter.difficulty;
    const ChIcon = CHAPTER_ICONS[selectedChapter.id] || BookOpen;

    return (
      <DashboardLayout auth={auth}>
        <div className="max-w-4xl mx-auto px-4 py-6">
          {/* Back button */}
          <button onClick={() => { setSelectedChapter(null); setChapterContent(null); fetchAll(); }}
            className="flex items-center gap-1 text-gray-600 hover:text-gray-900 mb-4">
            <ChevronLeft className="w-5 h-5" /> Back to Study Hub
          </button>

          {/* Chapter header */}
          <Card className="p-5 mb-6">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-blue-50">
                <ChIcon className="w-7 h-7 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-xl font-bold">{selectedChapter.title || `Chapter ${selectedChapter.id}`}</h1>
                  {difficulty && <span className={`text-xs px-2 py-0.5 rounded-full ${DIFFICULTY_STYLES[difficulty] || "bg-gray-100"}`}>{difficulty}</span>}
                </div>
                {selectedChapter.summary && <p className="text-sm text-gray-600 mt-1">{selectedChapter.summary}</p>}
                {/* Progress bar */}
                <div className="mt-3">
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                    <span>{readCount}/{totalSections} sections read</span>
                    <span>{Math.round((readCount / Math.max(totalSections, 1)) * 100)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-green-500 h-2 rounded-full transition-all" style={{ width: `${(readCount / Math.max(totalSections, 1)) * 100}%` }} />
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {chapterLoading ? (
            <div className="text-center py-12 text-gray-400">Loading chapter content...</div>
          ) : (
            <>
              {/* Chapter sub-tabs */}
              <Tabs value={chapterTab} onValueChange={setChapterTab} className="mb-6">
                <TabsList className="w-full flex">
                  <TabsTrigger value="content" className="flex-1"><BookOpen className="w-4 h-4 mr-1" /> Content</TabsTrigger>
                  <TabsTrigger value="keypoints" className="flex-1"><Target className="w-4 h-4 mr-1" /> Key Points</TabsTrigger>
                  <TabsTrigger value="tips" className="flex-1"><Lightbulb className="w-4 h-4 mr-1" /> Tips</TabsTrigger>
                  <TabsTrigger value="mnemonics" className="flex-1"><Brain className="w-4 h-4 mr-1" /> Mnemonics</TabsTrigger>
                </TabsList>

                {/* CONTENT TAB */}
                <TabsContent value="content">
                  <div className="space-y-3">
                    {sections.length === 0 && <p className="text-gray-400 py-8 text-center">No content available yet.</p>}
                    {sections.map((section, si) => {
                      const isRead = sectionsRead.includes(si);
                      const isOpen = expandedSections[si];
                      return (
                        <Card key={si} className={`overflow-hidden ${isRead ? "border-green-200" : ""}`}>
                          <button className="w-full p-4 flex items-center gap-3 text-left hover:bg-gray-50" onClick={() => setExpandedSections(p => ({ ...p, [si]: !p[si] }))}>
                            {isRead ? <CheckCircle className="w-5 h-5 text-green-500 shrink-0" /> : <CircleDot className="w-5 h-5 text-gray-300 shrink-0" />}
                            <span className="flex-1 font-medium">{section.title || `Section ${si + 1}`}</span>
                            {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                          </button>
                          {isOpen && (
                            <div className="px-5 pb-5 border-t">
                              {/* Section content */}
                              <div className="prose prose-sm max-w-none mt-3 text-gray-700 whitespace-pre-line">{section.content}</div>

                              {/* Test alerts */}
                              {section.test_alerts?.map((alert, ai) => (
                                <div key={ai} className="mt-3 p-3 bg-red-50 border-l-4 border-red-500 rounded-r-lg flex items-start gap-2">
                                  <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                                  <div>
                                    <span className="text-xs font-bold text-red-600 uppercase">Test Alert</span>
                                    <p className="text-sm text-red-800">{alert}</p>
                                  </div>
                                </div>
                              ))}

                              {/* Comparison tables */}
                              {section.comparison_tables?.map((tbl, ti) => (
                                <div key={ti} className="mt-4 overflow-x-auto">
                                  {tbl.title && <h5 className="font-medium text-sm mb-2">{tbl.title}</h5>}
                                  <table className="w-full text-sm border-collapse border">
                                    {tbl.headers && (
                                      <thead><tr>{tbl.headers.map((h, hi) => <th key={hi} className="p-2 bg-gray-50 border text-left font-medium">{h}</th>)}</tr></thead>
                                    )}
                                    <tbody>
                                      {tbl.rows?.map((row, ri) => (
                                        <tr key={ri}>{(Array.isArray(row) ? row : Object.values(row)).map((cell, ci) => <td key={ci} className="p-2 border">{cell}</td>)}</tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ))}

                              {/* Mark as read button */}
                              {!isRead && (
                                <Button onClick={() => markSectionRead(selectedChapter.id, si)} className="mt-4" size="sm">
                                  <CheckCircle className="w-4 h-4 mr-1" /> Mark as Read (+10 XP)
                                </Button>
                              )}
                            </div>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                </TabsContent>

                {/* KEY POINTS TAB */}
                <TabsContent value="keypoints">
                  <div className="space-y-3">
                    {keyPoints.length === 0 && <p className="text-gray-400 py-8 text-center">No key points available.</p>}
                    {keyPoints.map((kp, i) => {
                      const text = typeof kp === "string" ? kp : kp.text;
                      const freq = typeof kp === "string" ? null : kp.test_frequency;
                      const badge = freq ? FREQ_BADGE[freq] : null;
                      return (
                        <Card key={i} className="p-4 flex items-start gap-3">
                          <Target className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
                          <div className="flex-1">
                            <p className="text-sm">{text}</p>
                          </div>
                          {badge && <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap shrink-0 ${badge.cls}`}>{badge.label}</span>}
                        </Card>
                      );
                    })}
                  </div>
                </TabsContent>

                {/* TIPS TAB */}
                <TabsContent value="tips">
                  <div className="space-y-3">
                    {studyTips.length === 0 && <p className="text-gray-400 py-8 text-center">No study tips available.</p>}
                    {studyTips.map((tip, i) => (
                      <Card key={i} className="p-4 flex items-start gap-3">
                        <Lightbulb className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
                        <p className="text-sm">{typeof tip === "string" ? tip : tip.text || tip.tip}</p>
                      </Card>
                    ))}
                  </div>
                </TabsContent>

                {/* MNEMONICS TAB */}
                <TabsContent value="mnemonics">
                  <div className="space-y-3">
                    {mnemonics.length === 0 && <p className="text-gray-400 py-8 text-center">No memory aids available for this chapter.</p>}
                    {mnemonics.map((m, i) => (
                      <Card key={i} className="p-4 border-l-4 border-purple-400 bg-purple-50/30">
                        <div className="flex items-start gap-3">
                          <Brain className="w-6 h-6 text-purple-500 mt-0.5 shrink-0" />
                          <div>
                            {m.title && <h4 className="font-bold text-purple-800 text-sm">{m.title}</h4>}
                            <p className="text-sm text-gray-700 mt-1">{typeof m === "string" ? m : m.content || m.text}</p>
                            {m.explanation && <p className="text-xs text-gray-500 mt-1 italic">{m.explanation}</p>}
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>

              {/* Practice Questions link */}
              <div className="mt-6">
                <Button onClick={() => window.location.href = `/chapter-practice?chapter=${selectedChapter.id}`} variant="outline" className="w-full">
                  <GraduationCap className="w-4 h-4 mr-2" /> Practice Questions ({selectedChapter.question_count || "All"})
                </Button>
              </div>
            </>
          )}
        </div>
      </DashboardLayout>
    );
  }

  // ===== MAIN STUDY HUB VIEW =====
  return (
    <DashboardLayout auth={auth}>
      <div className="max-w-6xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold mb-6">Study Hub</h1>

        {loading ? (
          <div className="text-center py-16 text-gray-400">Loading study materials...</div>
        ) : (
          <>
            {/* === TOP PROGRESS OVERVIEW === */}
            <Card className="p-5 mb-6">
              <div className="flex flex-wrap items-center gap-6">
                {/* Readiness Gauge */}
                <div className="flex flex-col items-center">
                  <ReadinessGauge score={readiness} />
                  <span className="text-xs text-gray-500 mt-1">Test Readiness</span>
                </div>

                {/* XP + Level */}
                <div className="flex-1 min-w-[200px] space-y-3">
                  <XPBar xp={xpData.xp || 0} level={xpData.level || 0} />
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1.5">
                      <BookOpen className="w-4 h-4 text-blue-500" />
                      <span className="font-medium">{chaptersComplete}/{chapters.length}</span>
                      <span className="text-gray-500">chapters</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Flame className="w-4 h-4 text-orange-500" />
                      <span className="font-medium">{streak}</span>
                      <span className="text-gray-500">day streak</span>
                    </div>
                  </div>
                  {/* Badges */}
                  {xpData.badges?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {xpData.badges.slice(0, 5).map((b, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 flex items-center gap-1">
                          <Award className="w-3 h-3" /> {typeof b === "string" ? b : b.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Daily Challenge */}
                <div>
                  <Button onClick={() => setShowDailyChallenge(true)} className="bg-amber-500 hover:bg-amber-600 text-white">
                    <Trophy className="w-4 h-4 mr-1" /> Daily Challenge
                  </Button>
                </div>
              </div>
            </Card>

            {/* === MAIN TABS === */}
            <Tabs value={mainTab} onValueChange={setMainTab}>
              <TabsList className="w-full flex mb-6">
                <TabsTrigger value="chapters" className="flex-1"><BookOpen className="w-4 h-4 mr-1" /> Chapters</TabsTrigger>
                <TabsTrigger value="dates" className="flex-1"><Calendar className="w-4 h-4 mr-1" /> Dates</TabsTrigger>
                <TabsTrigger value="people" className="flex-1"><Users className="w-4 h-4 mr-1" /> People</TabsTrigger>
                <TabsTrigger value="visual" className="flex-1"><LayoutGrid className="w-4 h-4 mr-1" /> Visual Aids</TabsTrigger>
              </TabsList>

              {/* === CHAPTERS TAB === */}
              <TabsContent value="chapters">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {chapters.map(ch => {
                    const prog = getChapterProgress(ch.id);
                    const ChIcon = CHAPTER_ICONS[ch.id] || BookOpen;
                    return (
                      (() => {
                        const chNum = parseInt(String(ch.id).replace(/\D/g, "")) || 1;
                        const colors = CHAPTER_COLORS[chNum] || CHAPTER_COLORS[1];
                        return (
                          <Card key={ch.id} className={`p-4 cursor-pointer hover:shadow-lg transition-all group border-l-4 ${colors.bg} ${colors.border}`} onClick={() => openChapter(ch)}>
                            <div className="flex items-start gap-3">
                              <ProgressRing progress={prog} />
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-sm group-hover:text-blue-600 transition-colors line-clamp-2">
                                  {ch.title || `Chapter ${ch.id}`}
                                </h3>
                                <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                                  {ch.difficulty && <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${colors.badge}`}>{ch.difficulty}</span>}
                                  {ch.question_count && (
                                    <span className="text-[10px] text-gray-500 flex items-center gap-0.5">
                                      <HelpCircle className="w-3 h-3" /> {ch.question_count}
                                    </span>
                                  )}
                                  {ch.exam_weight && (
                                    <span className="text-[10px] text-gray-500 flex items-center gap-0.5">
                                      <BarChart3 className="w-3 h-3" /> {ch.exam_weight}%
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className={`p-1.5 rounded-lg ${colors.badge}`}>
                                <ChIcon className={`w-5 h-5 ${colors.icon} shrink-0`} />
                              </div>
                            </div>
                            {ch.summary && <p className="text-xs text-gray-500 mt-2 line-clamp-2">{ch.summary}</p>}
                          </Card>
                        );
                      })()
                    );
                  })}
                </div>
              </TabsContent>

              {/* === DATES TAB === */}
              <TabsContent value="dates">
                {/* Era filters */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {eras.map(era => (
                    <Button key={era} variant={eraFilter === era ? "default" : "outline"} size="sm"
                      onClick={() => setEraFilter(era)}
                      className={eraFilter === era ? "" : "text-gray-600"}>
                      {era}
                    </Button>
                  ))}
                </div>
                {/* Search */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input type="text" placeholder="Search dates..." value={dateSearch} onChange={e => setDateSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                </div>
                {/* Timeline */}
                {Object.keys(groupedDates).length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No dates match your filters.</p>
                ) : (
                  <div className="space-y-6">
                    {Object.entries(groupedDates).map(([era, items]) => {
                      const eraColor = ERA_COLORS[era] || "gray";
                      return (
                        <div key={era}>
                          <h3 className={`font-bold text-sm uppercase tracking-wider mb-3 text-${eraColor}-600`}>{era}</h3>
                          <div className={`space-y-2 border-l-4 border-${eraColor}-300 pl-4`}>
                            {items.map((d, i) => (
                              <div key={i} className="flex items-start gap-3 py-2">
                                <div className={`w-3 h-3 mt-1.5 rounded-full bg-${eraColor}-400 shrink-0 -ml-[22px]`} />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-bold text-sm">{d.date || d.year}</span>
                                    {d.test_frequency === "high" && <Star className="w-4 h-4 text-amber-500 fill-amber-500" />}
                                  </div>
                                  <p className="text-sm text-gray-700">{d.event || d.description || d.title}</p>
                                  {d.significance && <p className="text-xs text-gray-500 mt-0.5">{d.significance}</p>}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              {/* === PEOPLE TAB === */}
              <TabsContent value="people">
                {/* Role filters */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {roles.map(role => (
                    <Button key={role} variant={roleFilter === role ? "default" : "outline"} size="sm"
                      onClick={() => setRoleFilter(role)}
                      className={roleFilter === role ? "" : "text-gray-600"}>
                      {role}
                    </Button>
                  ))}
                </div>
                {/* Search */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input type="text" placeholder="Search people..." value={peopleSearch} onChange={e => setPeopleSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                </div>
                {Object.keys(groupedPeople).length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No people match your filters.</p>
                ) : (
                  <div className="space-y-6">
                    {Object.entries(groupedPeople).map(([era, items]) => {
                      const eraColor = ERA_COLORS[era] || "gray";
                      return (
                        <div key={era}>
                          <h3 className={`font-bold text-sm uppercase tracking-wider mb-3 text-${eraColor}-600`}>{era}</h3>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {items.map((p, i) => {
                              const roleColor = ROLE_COLORS[p.role] || "gray";
                              return (
                                <Card key={i} className="p-4">
                                  <div className="flex items-start justify-between gap-2">
                                    <div>
                                      <h4 className="font-bold text-sm">{p.name}</h4>
                                      <div className="flex flex-wrap items-center gap-1.5 mt-1">
                                        {p.role && <span className={`text-[10px] px-1.5 py-0.5 rounded-full bg-${roleColor}-100 text-${roleColor}-700`}>{p.role}</span>}
                                        {p.era && <span className={`text-[10px] px-1.5 py-0.5 rounded-full bg-${eraColor}-100 text-${eraColor}-700`}>{p.era}</span>}
                                        {p.test_frequency === "high" && (
                                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 flex items-center gap-0.5">
                                            <Star className="w-3 h-3 fill-current" /> Frequently Tested
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <Users className={`w-5 h-5 text-${roleColor}-400 shrink-0`} />
                                  </div>
                                  {p.description && <p className="text-xs text-gray-600 mt-2 line-clamp-3">{p.description}</p>}
                                  {p.chapter && <p className="text-[10px] text-gray-400 mt-2">Appears in: Chapter {p.chapter}</p>}
                                </Card>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              {/* === VISUAL AIDS TAB === */}
              <TabsContent value="visual">
                <div className="flex flex-wrap gap-2 mb-6">
                  {[
                    { id: "government", label: "Government", icon: Landmark },
                    { id: "map", label: "Canada Map", icon: MapPin },
                    { id: "comparisons", label: "Comparisons", icon: Scale },
                    { id: "timeline", label: "Confederation", icon: Clock },
                  ].map(vt => (
                    <Button key={vt.id} variant={visualTab === vt.id ? "default" : "outline"} size="sm" onClick={() => setVisualTab(vt.id)}>
                      <vt.icon className="w-4 h-4 mr-1" /> {vt.label}
                    </Button>
                  ))}
                </div>
                <Card className="p-5">
                  {visualTab === "government" && <GovernmentDiagram />}
                  {visualTab === "map" && <CanadaMap />}
                  {visualTab === "comparisons" && <ComparisonCards />}
                  {visualTab === "timeline" && <ConfederationTimeline />}
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}

        {/* Daily Challenge Modal */}
        {showDailyChallenge && <DailyChallengeModal auth={auth} onClose={() => { setShowDailyChallenge(false); fetchAll(); }} />}
      </div>
    </DashboardLayout>
  );
}


class StudyHubErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error("StudyHub crashed:", error, errorInfo);
    this.setState({ errorInfo });
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: "40px", textAlign: "center", fontFamily: "sans-serif" }}>
          <h2 style={{ color: "#dc2626", marginBottom: "16px" }}>Something went wrong loading the Study Hub</h2>
          <p style={{ color: "#6b7280", marginBottom: "16px" }}>We encountered an error. Please try refreshing the page.</p>
          <details style={{ textAlign: "left", maxWidth: "600px", margin: "0 auto", padding: "16px", background: "#f9fafb", borderRadius: "8px" }}>
            <summary style={{ cursor: "pointer", fontWeight: "bold" }}>Error Details</summary>
            <pre style={{ fontSize: "12px", overflow: "auto", marginTop: "8px", color: "#991b1b" }}>
              {this.state.error && this.state.error.toString()}
            </pre>
            <pre style={{ fontSize: "11px", overflow: "auto", marginTop: "4px", color: "#6b7280" }}>
              {this.state.errorInfo && this.state.errorInfo.componentStack}
            </pre>
          </details>
          <button onClick={() => window.location.reload()} style={{ marginTop: "20px", padding: "10px 24px", background: "#2563eb", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px" }}>
            Refresh Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function StudyHub(props) {
  return (
    <StudyHubErrorBoundary>
      <StudyHubInner {...props} />
    </StudyHubErrorBoundary>
  );
}
